import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Folder, Edit, Trash2, Plus, Loader2, ArrowLeft, Search } from 'lucide-react';
import { Link } from "react-router-dom";
import { toast } from 'sonner';
import { cn } from "../lib/utils";
import * as adminApi from '../services/adminApi';

export default function AdminFolders() {
    const queryClient = useQueryClient();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingFolder, setEditingFolder] = useState(null);
    const [formData, setFormData] = useState({ name: '', code: '', is_active: true });

    // Fetch Folders
    const { data: folders = [], isLoading } = useQuery({
        queryKey: ['adminFolders'],
        queryFn: () => adminApi.getAllFolders(),
    });

    // Create Folder Mutation
    const createMutation = useMutation({
        mutationFn: (newFolder) => adminApi.createFolder(newFolder),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminFolders'] });
            setIsDialogOpen(false);
            resetForm();
            toast.success('Folder created successfully');
        },
    });

    // Update Folder Mutation
    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => adminApi.updateFolder(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminFolders'] });
            setIsDialogOpen(false);
            resetForm();
            toast.success('Folder updated successfully');
        },
    });

    // Delete Folder Mutation
    const deleteMutation = useMutation({
        mutationFn: (id) => adminApi.deleteFolder(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminFolders'] });
            toast.success('Folder deleted');
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (editingFolder) {
            updateMutation.mutate({ id: editingFolder.folder_id, data: formData });
        } else {
            createMutation.mutate(formData);
        }
    };

    const handleEdit = (folder) => {
        setEditingFolder(folder);
        setFormData({ name: folder.name, code: folder.code, is_active: folder.is_active });
        setIsDialogOpen(true);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this folder?')) {
            deleteMutation.mutate(id);
        }
    };

    const resetForm = () => {
        setEditingFolder(null);
        setFormData({ name: '', code: '', is_active: true });
    };

    return (
        <div className="min-h-screen bg-slate-900 p-8">
            <div className="max-w-6xl mx-auto space-y-8">
                {/* Header */}
                <div className="space-y-1">
                    <div className="flex items-center gap-4 mb-2">
                        <Link to="/admin/indicator-alerts">
                            <Button variant="outline" size="sm" className="gap-2 bg-slate-900 border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800">
                                <ArrowLeft className="h-4 w-4" />
                                Back to TV Alert
                            </Button>
                        </Link>
                        <h1 className="text-2xl font-bold text-white">Folder Management</h1>
                    </div>
                    <p className="text-slate-400 pl-14">Admin only - Create and manage signal folders</p>
                </div>

                {/* Main Card */}
                <Card className="bg-slate-950 border-slate-800">
                    <CardHeader className="flex flex-row items-center justify-between border-b border-slate-800 px-6 py-4">
                        <CardTitle className="text-white text-lg font-semibold">Signal Folders</CardTitle>
                        <Dialog open={isDialogOpen} onOpenChange={(open) => {
                            setIsDialogOpen(open);
                            if (!open) resetForm();
                        }}>
                            <DialogTrigger asChild>
                                <Button className="bg-blue-600 hover:bg-blue-700 gap-2">
                                    <Plus className="w-4 h-4" />
                                    Add Folder
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-900 border-slate-800 text-white">
                                <DialogHeader>
                                    <DialogTitle>{editingFolder ? 'Edit Folder' : 'Add New Folder'}</DialogTitle>
                                </DialogHeader>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>Folder Name</Label>
                                        <Input
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="bg-slate-800 border-slate-700"
                                            placeholder="e.g. Gold Scalper"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>System Code</Label>
                                        <Input
                                            value={formData.code}
                                            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                            className="bg-slate-800 border-slate-700"
                                            placeholder="e.g. GOLD_SC"
                                            required
                                        />
                                    </div>
                                    <div className="flex items-center justify-between rounded-lg border border-slate-800 p-3 bg-slate-950">
                                        <div className="space-y-0.5">
                                            <Label>Active Status</Label>
                                            <div className="text-sm text-slate-400">Enable or disable this system</div>
                                        </div>
                                        <Switch
                                            checked={formData.is_active}
                                            onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                                        />
                                    </div>
                                    <DialogFooter className="mt-4">
                                        <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                                            Cancel
                                        </Button>
                                        <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                                            {editingFolder ? 'Update Folder' : 'Create Folder'}
                                        </Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader className="bg-slate-900/50">
                                <TableRow className="border-slate-800 hover:bg-transparent">
                                    <TableHead className="text-slate-400 pl-6 h-12">Folder</TableHead>
                                    <TableHead className="text-slate-400">System Code</TableHead>
                                    <TableHead className="text-slate-400">Alerts</TableHead>
                                    <TableHead className="text-slate-400">Status</TableHead>
                                    <TableHead className="text-right text-slate-400 pr-6">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8">
                                            <Loader2 className="w-6 h-6 text-blue-500 animate-spin mx-auto" />
                                        </TableCell>
                                    </TableRow>
                                ) : folders.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                                            No folders found. Create one to get started.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    folders.map((folder) => (
                                        <TableRow key={folder.folder_id} className="border-slate-800 hover:bg-slate-900/50">
                                            <TableCell className="pl-6 font-medium text-white">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center text-blue-400">
                                                        <Folder className="w-4 h-4" />
                                                    </div>
                                                    {folder.name}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-slate-400 font-mono text-sm">
                                                {folder.code}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="secondary" className="bg-slate-800 text-slate-300 hover:bg-slate-800">
                                                    {folder.total_alert_count || 0}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={cn(
                                                    "font-normal",
                                                    folder.is_active
                                                        ? "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                                                        : "bg-red-500/10 text-red-400 hover:bg-red-500/20"
                                                )}>
                                                    {folder.is_active ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right pr-6">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-slate-400 hover:text-blue-400 hover:bg-blue-900/20"
                                                        onClick={() => handleEdit(folder)}
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-slate-400 hover:text-red-400 hover:bg-red-900/20"
                                                        onClick={() => handleDelete(folder.folder_id)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
